import React, { useState } from 'react';
import { Mail, GripVertical, Edit2, Trash2, Save, X, Plus, User } from 'lucide-react';
import '../styles/contact-manager.css';

const ContactManager = ({ contacts, onUpdate }) => {
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [draggedItem, setDraggedItem] = useState(null);

  const startEdit = (contact) => {
    setEditingId(contact.id);
    setFormData({
      name: contact.name,
      email: contact.email
    });
  };

  const startAdd = () => {
    setAddingNew(true);
    setFormData({ name: '', email: '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setAddingNew(false);
    setFormData({ name: '', email: '' });
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      alert('Please fill in all fields');
      return;
    }

    if (editingId) {
      await onUpdate('update', editingId, {
        name: formData.name,
        email_addr: formData.email
      });
    } else {
      await onUpdate('create', null, {
        name: formData.name,
        email: formData.email
      });
    }
    cancelEdit();
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      await onUpdate('delete', contactId);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Drag and Drop handlers
  const handleDragStart = (e, contact) => {
    setDraggedItem(contact);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetContact) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetContact.id) {
      setDraggedItem(null);
      return;
    }

    // Reorder contacts
    const contactsCopy = [...contacts];
    const draggedIndex = contactsCopy.findIndex(c => c.id === draggedItem.id);
    const targetIndex = contactsCopy.findIndex(c => c.id === targetContact.id);

    // Remove dragged item and insert at new position
    const [removed] = contactsCopy.splice(draggedIndex, 1);
    contactsCopy.splice(targetIndex, 0, removed);

    // Get new order as array of IDs
    const newOrder = contactsCopy.map(c => c.id);

    // Update via API
    await onUpdate('reorder', null, newOrder);
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  return (
    <div className="contact-manager">
      <div className="contact-manager-header">
        <h3>Manage Contacts</h3>
        {!addingNew && !editingId && (
          <button onClick={startAdd} className="add-contact-btn">
            <Plus size={18} />
            Add Contact
          </button>
        )}
      </div>

      {/* Add New Contact Form */}
      {addingNew && (
        <div className="contact-form">
          <h4>Add New Contact</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@example.com"
              />
            </div>
          </div>
          <div className="form-actions">
            <button onClick={handleSave} className="save-btn">
              <Save size={16} />
              Save Contact
            </button>
            <button onClick={cancelEdit} className="cancel-btn">
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Contact List */}
      <div className="contact-list">
        {contacts && contacts.length > 0 ? (
          contacts
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((contact) => (
              <div
                key={contact.id}
                className={`contact-item ${draggedItem?.id === contact.id ? 'dragging' : ''}`}
                draggable={editingId !== contact.id && !addingNew}
                onDragStart={(e) => handleDragStart(e, contact)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, contact)}
                onDragEnd={handleDragEnd}
              >
                {editingId === contact.id ? (
                  /* Edit Form */
                  <div className="contact-edit-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-actions">
                      <button onClick={handleSave} className="save-btn">
                        <Save size={16} />
                        Save
                      </button>
                      <button onClick={cancelEdit} className="cancel-btn">
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <>
                    <div className="contact-grip">
                      <GripVertical size={20} />
                    </div>
                    <div className="contact-info">
                      <div className="contact-name">
                        <User size={16} />
                        {contact.name}
                      </div>
                      <div className="contact-email">
                        <Mail size={14} />
                        {contact.email}
                      </div>
                    </div>
                    <div className="contact-actions">
                      <button
                        onClick={() => startEdit(contact)}
                        className="edit-btn"
                        title="Edit contact"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="delete-btn"
                        title="Delete contact"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
        ) : (
          <p className="no-contacts">No contacts yet. Add your first contact above.</p>
        )}
      </div>
    </div>
  );
};

export default ContactManager;
